import React, { useState } from 'react';
import { Box, Flex, Spinner, Text } from '@chakra-ui/react';

import { GlassCard } from '../../../../../components/glass';
import { useContaCilFechamentos } from '../../../../../hooks/useContaCilDashboard';
import { FechamentoDetalheModal } from './FechamentoDetalheModal';

const MESES_ABBR = [
  'JAN',
  'FEV',
  'MAR',
  'ABR',
  'MAI',
  'JUN',
  'JUL',
  'AGO',
  'SET',
  'OUT',
  'NOV',
  'DEZ',
];
const ACCENT = '#7C3AED';

// Faixa de cor por nº de empresas pendentes (quanto mais escuro, mais pendências).
function fechColor(p: number | null): { bg: string; fg: string } {
  if (p === null) return { bg: '#F3F4F9', fg: '#C3C6D8' }; // futuro
  if (p === 0) return { bg: 'rgba(16, 163, 113, 0.14)', fg: '#0EA371' }; // em dia
  if (p <= 5) return { bg: '#FDF3D8', fg: '#9A7B1E' };
  if (p <= 15) return { bg: '#FBE0B0', fg: '#8A5A12' };
  if (p <= 40) return { bg: '#F2A65A', fg: '#5A3410' };
  return { bg: '#D24B3E', fg: '#FFFFFF' };
}

const GRID = '52px repeat(12, minmax(0, 1fr))';

interface FechamentosCalendarProps {
  ate: number; // ano de referência (o mais recente exibido)
}

const SpanButton = ({ n, ativo, onClick }: { n: number; ativo: boolean; onClick: () => void }) => (
  <Box
    as="button"
    px={3}
    py={1}
    borderRadius="lg"
    fontSize="12px"
    fontWeight="600"
    bg={ativo ? 'white' : 'transparent'}
    color={ativo ? '#0F172A' : '#64748B'}
    boxShadow={ativo ? '0 1px 3px rgba(15,23,42,0.12)' : 'none'}
    onClick={onClick}
  >
    {n} {n === 1 ? 'ano' : 'anos'}
  </Box>
);

export const FechamentosCalendar = ({ ate }: FechamentosCalendarProps) => {
  const [anos, setAnos] = useState(3);
  const [sel, setSel] = useState<{ ano: number; mes: number } | null>(null);
  const { data, isLoading } = useContaCilFechamentos(ate, anos);
  const hoje = new Date();
  const mesAtual = hoje.getMonth() + 1;

  return (
    <GlassCard intensity="medium" p={6}>
      <Flex justify="space-between" align="flex-start" gap={4} mb={5} wrap="wrap">
        <Box>
          <Text
            fontSize="10px"
            fontWeight="600"
            color="#94A3B8"
            textTransform="uppercase"
            letterSpacing="0.08em"
            mb={1}
          >
            Fechamento contábil
          </Text>
          <Text fontSize="18px" fontWeight="700" color="#0F172A" letterSpacing="-0.01em">
            Empresas pendentes por competência
          </Text>
        </Box>
        <Flex bg="#EEF0F6" borderRadius="xl" p={1} gap={1} flexShrink={0}>
          {[1, 2, 3].map(n => (
            <SpanButton key={n} n={n} ativo={anos === n} onClick={() => setAnos(n)} />
          ))}
        </Flex>
      </Flex>

      {isLoading || !data ? (
        <Flex justify="center" py={10}>
          <Spinner color={ACCENT} />
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Box minW="760px">
            {/* header de meses */}
            <Box display="grid" gridTemplateColumns={GRID} gap={1.5} mb={1.5}>
              <Box />
              {MESES_ABBR.map((m, i) => {
                const isAtual = i + 1 === mesAtual && data.anos[data.anos.length - 1]?.ano === ate;
                return (
                  <Text
                    key={m}
                    fontSize="9.5px"
                    fontWeight="700"
                    textAlign="center"
                    letterSpacing="0.04em"
                    color={isAtual ? ACCENT : '#94A3B8'}
                  >
                    {m}
                  </Text>
                );
              })}
            </Box>
            {/* uma linha por ano (mais recente por último) */}
            {data.anos.map(anoRow => (
              <Box key={anoRow.ano} display="grid" gridTemplateColumns={GRID} gap={1.5} mb={1.5}>
                <Flex align="center" justify="flex-start">
                  <Text fontSize="12px" fontWeight="700" color="#64748B">
                    {anoRow.ano}
                  </Text>
                </Flex>
                {anoRow.meses.map(cel => {
                  const { bg, fg } = fechColor(cel.pendentes);
                  const isAtual = cel.mes === mesAtual && anoRow.ano === ate;
                  const clicavel = cel.pendentes !== null;
                  return (
                    <Flex
                      key={cel.mes}
                      as={clicavel ? 'button' : 'div'}
                      align="center"
                      justify="center"
                      h="34px"
                      borderRadius="8px"
                      bg={bg}
                      color={fg}
                      fontSize="12px"
                      fontWeight="700"
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                      outline={isAtual ? `2px solid ${ACCENT}` : 'none'}
                      outlineOffset="1px"
                      cursor={clicavel ? 'pointer' : 'default'}
                      transition="transform 0.12s, box-shadow 0.12s"
                      _hover={
                        clicavel
                          ? {
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 10px -4px rgba(15,23,42,0.3)',
                            }
                          : undefined
                      }
                      onClick={
                        clicavel ? () => setSel({ ano: anoRow.ano, mes: cel.mes }) : undefined
                      }
                    >
                      {cel.pendentes === null ? '·' : cel.pendentes}
                    </Flex>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* legenda + totais */}
      {data && (
        <Flex justify="space-between" align="center" mt={4} wrap="wrap" gap={3}>
          <Flex align="center" gap={3} fontSize="11px" color="#64748B" wrap="wrap">
            <Flex align="center" gap={1.5}>
              <Box w="12px" h="12px" borderRadius="4px" bg="rgba(16,163,113,0.2)" />
              <Text>Em dia</Text>
            </Flex>
            <Flex align="center" gap={1}>
              <Text color="#94A3B8">Pendentes</Text>
              {['#FDF3D8', '#FBE0B0', '#F2A65A', '#D24B3E'].map(c => (
                <Box key={c} w="12px" h="12px" borderRadius="4px" bg={c} />
              ))}
              <Text color="#94A3B8">mais escuro = mais empresas</Text>
            </Flex>
          </Flex>
          <Text fontSize="11px" color="#64748B" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            Total: <b style={{ color: '#0F172A' }}>{data.total}</b> pendências · pico{' '}
            <b style={{ color: '#0F172A' }}>{data.pico}</b> num único mês
          </Text>
        </Flex>
      )}

      <FechamentoDetalheModal
        ano={sel?.ano ?? ate}
        mes={sel?.mes ?? null}
        isOpen={sel !== null}
        onClose={() => setSel(null)}
      />
    </GlassCard>
  );
};
